import classNames from 'classnames';

import Icons from 'assets/icons';

import { useOpen } from 'hooks/use-open';

import type { FeatureHintTargetProps } from 'components/shared/feature-hints';

import MarkContextTrigger from 'containers/admin/clients/client/tabs-components/ai-assistant/mark-panel/components/mark-context-trigger';

import type { StageCardDisplayMode } from 'containers/user/critical-elements-monitoring/components/funnel-stage-card';
import CriticalElementSummaryStat from 'containers/user/critical-elements-monitoring/components/critical-element-summary-stat';
import MonitoringWarningAlert from 'containers/user/critical-elements-monitoring/components/monitoring-warning-alert';
import FunnelCard from 'containers/user/critical-elements-monitoring/components/funnel-card';
import FunnelCardRowSkeleton from 'containers/user/critical-elements-monitoring/components/funnel-card-row-skeleton';
import FunnelCardGrid from 'containers/user/critical-elements-monitoring/components/funnel-card-grid';
import { buildFunnelCardRow } from 'containers/user/critical-elements-monitoring/helpers/build-funnel-card-row';
import { getStageVolume } from 'containers/user/critical-elements-monitoring/helpers/get-stage-volume';
import {
  getExtensionDisplayLabel,
  getStageDisplayLabel,
  NO_MONITORING_DATA_MESSAGE,
} from 'containers/user/critical-elements-monitoring/monitoring-constants';
import { trackMonitoringCriticalElementToggled } from 'containers/user/critical-elements-monitoring/analytics/monitoring-analytics';
import {
  isMarkTriggerEligible,
  MONITORING_MARK_SECTION,
} from 'containers/user/critical-elements-monitoring/mark/monitoring-mark-context';
import type { ProspectAvailability } from 'containers/user/critical-elements-monitoring/helpers/get-prospect-availability';
import type {
  CompareTo,
  CriticalElementSummary,
  ExtendWithOption,
  FunnelSection,
} from 'containers/user/critical-elements-monitoring/critical-elements-monitoring.types';

export interface CriticalElementRowProps {
  summary: CriticalElementSummary;
  funnel: FunnelSection | undefined;
  compareTo: CompareTo;
  isFunnelLoading: boolean;
  totalInquiryVolume: number | undefined;
  totalApplicationVolume: number | undefined;
  totalNetDepositVolume: number | undefined;
  displayMode: StageCardDisplayMode;
  selectedUnentitledExtensions: readonly ExtendWithOption[];
  skeletonCardCount: number;
  prospectAvailability: ProspectAvailability;
  isMonitoringDataAbsent: boolean;
  isAnalyticsEnabled?: boolean;
  headerHintTargetProps?: FeatureHintTargetProps;
}

const ABSENT_STAGE_VOLUME = 0;

const CHEVRON_SPACER_CLASSES = 'size-3 shrink-0';

const CriticalElementRow = ({
  summary,
  funnel,
  compareTo,
  isFunnelLoading,
  totalInquiryVolume,
  totalApplicationVolume,
  totalNetDepositVolume,
  displayMode,
  selectedUnentitledExtensions,
  skeletonCardCount,
  prospectAvailability,
  isMonitoringDataAbsent,
  isAnalyticsEnabled = true,
  headerHintTargetProps,
}: CriticalElementRowProps) => {
  const { isOpen, handleToggle } = useOpen(false);
  const cardRow = funnel
    ? buildFunnelCardRow(funnel, selectedUnentitledExtensions)
    : [];

  const titleId = `critical-element-title-${summary.id}`;
  const contentId = `critical-element-content-${summary.id}`;
  const metaId = `critical-element-meta-${summary.id}`;
  const statsId = `critical-element-stats-${summary.id}`;

  const handleHeaderClick = (): void => {
    if (isAnalyticsEnabled) {
      trackMonitoringCriticalElementToggled({
        criticalElement: summary,
        action: isOpen ? 'collapsed' : 'expanded',
      });
    }
    handleToggle();
  };

  if (isMonitoringDataAbsent) {
    return (
      <div role="listitem">
        <div
          {...headerHintTargetProps}
          id={titleId}
          className={classNames(
            'flex w-full items-center gap-3 rounded-[10px] border border-navy-200 px-4 py-3 font-medium',
            headerHintTargetProps?.className
          )}
        >
          <span className={CHEVRON_SPACER_CLASSES} />
          <span className="flex min-w-0 flex-1 flex-col items-start text-left">
            <span className="font-semibold text-gray-700">{summary.name}</span>
            <span className="truncate text-sm text-gray-500">
              {summary.dimensionMeta}
            </span>
            <MonitoringWarningAlert message={NO_MONITORING_DATA_MESSAGE} />
          </span>
          <span className="flex shrink-0 items-center gap-4 text-right">
            <CriticalElementSummaryStat
              label="Inq."
              value={ABSENT_STAGE_VOLUME}
              totalVolume={totalInquiryVolume}
              isLoading={false}
            />
            <CriticalElementSummaryStat
              label="Apps"
              value={ABSENT_STAGE_VOLUME}
              totalVolume={totalApplicationVolume}
              isLoading={false}
            />
            <CriticalElementSummaryStat
              label="Net Dep."
              value={ABSENT_STAGE_VOLUME}
              totalVolume={totalNetDepositVolume}
              isLoading={false}
            />
          </span>
        </div>
      </div>
    );
  }

  return (
    <div role="listitem">
      <div
        {...headerHintTargetProps}
        className={classNames(
          'group relative flex items-center w-full py-3 px-4 rounded-[10px] font-medium border border-navy-200 gap-3 hover:bg-navy-50',
          { 'border-b-0 rounded-b-none': isOpen },
          headerHintTargetProps?.className
        )}
      >
        <Icons.ChevronRight
          className={classNames(
            'size-3 text-gray-600 shrink-0 transition-transform',
            {
              'rotate-90': isOpen,
            }
          )}
          aria-hidden="true"
        />
        <span className="flex flex-1 flex-col items-start text-left min-w-0">
          <span className="flex items-center gap-2">
            <button
              type="button"
              id={titleId}
              className="text-left font-semibold text-gray-700 after:absolute after:inset-0 after:content-[''] after:rounded-[10px] focus-visible:after:ring-2 focus-visible:after:ring-navy-800"
              onClick={handleHeaderClick}
              aria-expanded={isOpen}
              aria-controls={contentId}
              aria-describedby={`${metaId} ${statsId}`}
            >
              {summary.name}
            </button>
            <MarkContextTrigger
              target={{
                kind: 'entity',
                entityType: 'criticalElement',
                entityId: summary.id,
              }}
              ariaLabel={`Ask MARK about ${summary.name}`}
              className="relative z-10"
            />
          </span>
          <span id={metaId} className="text-sm text-gray-500 truncate">
            {summary.dimensionMeta}
          </span>
        </span>
        <span
          id={statsId}
          className="flex items-center gap-4 text-right shrink-0"
        >
          <CriticalElementSummaryStat
            label="Inq."
            value={getStageVolume(funnel, 'inquiry')}
            totalVolume={totalInquiryVolume}
            isLoading={isFunnelLoading}
          />
          <CriticalElementSummaryStat
            label="Apps"
            value={getStageVolume(funnel, 'application')}
            totalVolume={totalApplicationVolume}
            isLoading={isFunnelLoading}
          />
          <CriticalElementSummaryStat
            label="Net Dep."
            value={getStageVolume(funnel, 'netDeposit')}
            totalVolume={totalNetDepositVolume}
            isLoading={isFunnelLoading}
          />
        </span>
      </div>

      <div
        id={contentId}
        role="region"
        aria-labelledby={titleId}
        className={classNames({ hidden: !isOpen })}
      >
        <div className="flex w-0 min-w-full flex-col gap-4 p-4 border border-navy-200 border-t-0 rounded-b-[10px]">
          {!funnel && isFunnelLoading && (
            <FunnelCardRowSkeleton cardCount={skeletonCardCount} />
          )}

          {funnel && (
            <FunnelCardGrid>
              {cardRow.map((item) => (
                <FunnelCard
                  key={item.key}
                  item={item}
                  compareTo={compareTo}
                  displayMode={displayMode}
                  cohortLabel={funnel.cohortLabel}
                  prospectAvailability={prospectAvailability}
                  renderStageHeaderAction={(stage) =>
                    isMarkTriggerEligible(stage, displayMode) ? (
                      <MarkContextTrigger
                        target={{
                          kind: 'tile',
                          sectionId: MONITORING_MARK_SECTION.criticalElement,
                          entityId: summary.id,
                          tileId: stage.stageKey,
                          displayMode,
                        }}
                        ariaLabel={`Ask MARK about ${
                          summary.name
                        } ${getStageDisplayLabel(stage.stageKey, displayMode)}`}
                      />
                    ) : null
                  }
                  renderExtensionHeaderAction={(extension) =>
                    isMarkTriggerEligible(extension, displayMode) ? (
                      <MarkContextTrigger
                        target={{
                          kind: 'tile',
                          sectionId: MONITORING_MARK_SECTION.criticalElement,
                          entityId: summary.id,
                          tileId: extension.type,
                          displayMode,
                        }}
                        ariaLabel={`Ask MARK about ${
                          summary.name
                        } ${getExtensionDisplayLabel(
                          extension.type,
                          displayMode
                        )}`}
                      />
                    ) : null
                  }
                />
              ))}
            </FunnelCardGrid>
          )}
        </div>
      </div>
    </div>
  );
};

export default CriticalElementRow;
